import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { X } from 'lucide-react';
import { Card } from '../components/ui/card';
import { gallery } from '../data/mock';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState('all');

  const categories = ['all', 'exterior', 'rooms', 'restaurant', 'facilities'];
  
  const filteredGallery = filter === 'all' 
    ? gallery 
    : gallery.filter(img => img.category === filter);

  return (
    <>
      <Helmet>
        <title>{`Photo Gallery | Hotel Suvidha Deluxe Haridwar`}</title>
        <meta name="description" content="View real photos of rooms, reception, and facilities at Hotel Suvidha Deluxe Haridwar." />
      </Helmet>
    <div className="min-h-screen pt-32 pb-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1920&q=80)', backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 font-serif text-navy-900">Photo Gallery</h1>
          <p className="text-xl text-gold-400 max-w-3xl mx-auto leading-relaxed">
            Take a visual tour of our beautiful property and discover what awaits you
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="container mx-auto px-4 -mt-12">
        <Card className="border-0 shadow-2xl p-8">
          {/* Filter Tabs */}
          <Tabs defaultValue="all" className="mb-8">
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-5 bg-gray-100 p-1">
              <TabsTrigger value="all" onClick={() => setFilter('all')}>All</TabsTrigger>
              <TabsTrigger value="exterior" onClick={() => setFilter('exterior')}>Exterior</TabsTrigger>
              <TabsTrigger value="rooms" onClick={() => setFilter('rooms')}>Rooms</TabsTrigger>
              <TabsTrigger value="restaurant" onClick={() => setFilter('restaurant')}>Dining</TabsTrigger>
              <TabsTrigger value="facilities" onClick={() => setFilter('facilities')}>Facilities</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredGallery.map((image) => (
              <div
                key={image.id}
                className="relative aspect-[4/3] overflow-hidden rounded-lg shadow-lg cursor-pointer group"
                onClick={() => setSelectedImage(image)}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  loading="lazy"
                  width={400}
                  height={300}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  <p className="text-white font-semibold">{image.alt}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gold-500 transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <X size={32} />
          </button>
          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedImage.url}
              alt={selectedImage.alt}
              loading="lazy"
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
            />
            <p className="text-white text-center mt-4 text-lg">{selectedImage.alt}</p>
          </div>
        </div>
      )}

      {/* Virtual Tour CTA */}
      <section className="py-20 mt-20 bg-gradient-to-br from-navy-900 to-navy-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6 font-serif">Experience More</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Pictures don't do justice. Come and experience the warm hospitality in person!
          </p>
          <a href="/booking">
            <button className="bg-gold-600 hover:bg-gold-700 text-white px-10 py-4 rounded-lg text-lg font-semibold transition-colors">
              Book Your Stay
            </button>
          </a>
        </div>
      </section>
    </div>
    </>
  );
};

export default Gallery;
